// =============================================================================
// FieldProjectPanels — the crew's Documents and HOP panels (27 Sep 2026)
// =============================================================================
// Lifted out of FieldProjectDetailScreen so the Site folder can show them too
// (James, 27 Sep: "put the Documents and HOP library for this project … in the
// site folder … make the site folder the field's main project interaction").
// One copy of the code, two doors in.
// =============================================================================
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, ExternalLink, Camera, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { format, parseISO } from "date-fns";

// -----------------------------------------------------------------------------

export function DocumentsTab({ projectId }: { projectId: string }) {
  const [search, setSearch] = useState("");
  // T-309 (2026-06-24): the previous query filtered bid_documents on the
  // LEGACY doc_type field with a hardcoded FIELD_SAFE_TYPES list. After
  // migration 111 (T-161 document library overhaul) introduced
  // document_subcategories with per-row read_roles[] arrays, new uploads
  // populate subcategory_id and leave doc_type either NULL or set to a
  // subcategory code (RF_DESIGN, FC, GEO, etc.) — none of which matched the
  // legacy list. Result: every modern doc became invisible to field workers
  // even when they were assigned to the project.
  //
  // Fix: mirror the manager-side LibraryDocumentsTab pattern — load
  // document_subcategories, derive which subcategories include 'field' in
  // read_roles, then return bid_documents whose subcategory is in that
  // visible set (or has no subcategory at all, for legacy back-compat).
  // The seeded permissions already exclude Commercial pillar (DA / Tender /
  // Quote / PO / Variation / Bonds / Carrier Approvals) from field; everything
  // else (Design / SAED / Compliance / Site / Acceptance / Admin / Reference)
  // is field-accessible.
  const { data: subCats = [] } = useQuery({
    queryKey: ["doc_subcategories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_subcategories")
        .select("subcategory_id, code, display_name, read_roles")
        // Match LibraryDocumentsTab - without this, retired subcategories that
        // no longer include 'field' in read_roles still gated docs here.
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as Array<{
        subcategory_id: string;
        code: string;
        display_name: string;
        read_roles: string[];
      }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const visibleSubcategoryIds = useMemo(
    () => new Set(subCats.filter(s => s.read_roles?.includes("field")).map(s => s.subcategory_id)),
    [subCats],
  );
  const subcatById = useMemo(
    () => new Map(subCats.map(s => [s.subcategory_id, s])),
    [subCats],
  );

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["field_project_docs", projectId],
    enabled: subCats.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_documents")
        .select("id, file_name, file_path, file_size_bytes, mime_type, doc_type, title, uploaded_at, subcategory_id")
        .eq("project_id", projectId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      // Client-side permission filter: visible subcategories OR legacy
      // (subcategory_id IS NULL) so older uploads aren't lost. Subcategory
      // RLS would be cleaner but no bid_documents RLS exists today; revisit
      // when adding it.
      return ((data ?? []) as any[]).filter(d =>
        d.subcategory_id == null || visibleSubcategoryIds.has(d.subcategory_id),
      );
    },
  });

  async function openDoc(doc: any) {
    // James, 8 Sep 2026: docs stopped opening on phones (field + director).
    // Mobile browsers only allow a new tab opened SYNCHRONOUSLY in the tap
    // handler — after the await on createSignedUrl() the popup is silently
    // blocked. Open a blank window NOW, navigate it once we have the URL
    // (same pattern as LibraryDocumentsTab.handleDownload).
    // 9 Sep, whole crew: tab opened BLANK. "noopener" in the features makes
    // window.open return NULL, so we could never steer the tab we'd opened.
    // Open with no features (handle comes back), sever opener by hand.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    const { data, error } = await supabase.storage
      .from("bid-documents")
      .createSignedUrl(doc.file_path, 60 * 10);
    if (error || !data?.signedUrl) {
      if (win) win.close();
      alert(error?.message ?? "Could not load document");
      return;
    }
    const mime = doc.mime_type ?? "";
    const inlineable = mime === "application/pdf" || mime.startsWith("image/");
    const url = inlineable
      ? `${data.signedUrl}&response-content-disposition=inline`
      : data.signedUrl;
    if (win) win.location.href = url;
    else window.location.href = url;  // popup blocked entirely — same-tab fallback
  }

  function fmtBytes(n: number | null | undefined): string {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (isLoading) {
    return <div className="py-10 flex justify-center"><Spinner size={18} /></div>;
  }

  if (docs.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No documents yet"
          description="Designs, drawings, and scopes will show up here when the office uploads them."
          icon={FileText}
        />
      </Card>
    );
  }

  // 27 Sep (James, from the phone): "the document library could have headers
  // and be a bit easier to see and use on site". Grouped under the document
  // type (T-309: subcategory display_name, legacy doc_type as the fallback),
  // a search box for long lists, and a PDF / photo / file glyph so the eye
  // finds the drawing without reading every line.
  const q = search.trim().toLowerCase();
  const shown = q ? docs.filter(d => `${d.title ?? ""} ${d.file_name ?? ""}`.toLowerCase().includes(q)) : docs;
  const groups = new Map<string, any[]>();
  for (const d of shown) {
    const sub = d.subcategory_id ? subcatById.get(d.subcategory_id) : null;
    const label = sub?.display_name ?? d.doc_type ?? "Other";
    (groups.get(label) ?? groups.set(label, []).get(label)!).push(d);
  }
  const groupNames = [...groups.keys()].sort((a, b) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)));
  const kindOf = (mime: string | null | undefined, name: string) => {
    const m = mime ?? ""; const n = name.toLowerCase();
    if (m === "application/pdf" || n.endsWith(".pdf")) return { glyph: "PDF", cls: "text-danger border-danger/30 bg-danger/10" };
    if (m.startsWith("image/") || /\.(jpe?g|png|heic|gif|webp)$/.test(n)) return { glyph: "IMG", cls: "text-accent border-accent/30 bg-accent/10" };
    if (/\.(xlsx?|csv)$/.test(n) || m.includes("sheet") || m.includes("excel")) return { glyph: "XLS", cls: "text-ok border-ok/30 bg-ok/10" };
    if (/\.(docx?)$/.test(n) || m.includes("word")) return { glyph: "DOC", cls: "text-text-muted border-line bg-ink-raised" };
    return { glyph: "FILE", cls: "text-text-muted border-line bg-ink-raised" };
  };

  return (
    <div className="space-y-4">
      {docs.length > 6 && (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Find a document…"
                 className="w-full bg-ink-raised border border-line rounded-md pl-9 pr-3 py-2.5 text-[14px] text-text placeholder:text-text-dim focus:outline-none focus:border-accent" />
        </div>
      )}
      {shown.length === 0 && <div className="text-[13px] text-text-muted py-6 text-center">Nothing matches "{search}".</div>}
      {groupNames.map(name => (
        <div key={name}>
          <div className="flex items-center justify-between mb-1.5 px-1">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">{name}</div>
            <div className="text-[11px] text-text-dim">{groups.get(name)!.length}</div>
          </div>
          <Card>
            <div className="divide-y divide-line">
              {groups.get(name)!.map(d => {
                const k = kindOf(d.mime_type, d.file_name ?? "");
                return (
                  <button key={d.id} type="button" onClick={() => openDoc(d)} className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-ink-raised">
                    <div className={`w-11 h-11 rounded-md border flex items-center justify-center flex-shrink-0 text-[10px] font-bold font-mono ${k.cls}`}>{k.glyph}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-text leading-snug line-clamp-2 break-words">{d.title || d.file_name}</div>
                      <div className="text-[11px] text-text-muted mt-0.5">
                        {fmtBytes(d.file_size_bytes)}{d.uploaded_at && ` · ${format(parseISO(d.uploaded_at), "dd MMM yyyy")}`}
                      </div>
                    </div>
                    <ExternalLink size={14} className="text-text-muted flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------

export function HopTab({ projectId }: { projectId: string }) {
  // Lists the project's HOP packs with a deep-link into the library. Pack
  // structure (categories/subcategories) lives on the library screen — this
  // tab is the summary surface for field users checking "what do I need to
  // upload?". T-010 Commit C.1.
  const { data: packs = [], isLoading } = useQuery({
    queryKey: ["field_hop_packs", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_packs")
        .select(`
          pack_id, pack_number, title, status, created_at,
          template:template_id(template_code, display_name)
        `)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const packTone: Record<string, "open" | "pending" | "approved" | "rejected"> = {
    open:           "open",
    submitted:      "pending",
    accepted:       "approved",
    defects_raised: "rejected",
  };
  const packLabel: Record<string, string> = {
    open:           "in progress",
    submitted:      "awaiting review",
    accepted:       "accepted",
    defects_raised: "defects raised",
  };

  if (isLoading) {
    return <div className="py-10 flex justify-center"><Spinner size={18} /></div>;
  }

  if (packs.length === 0) {
    return (
      <Card>
        <div className="p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-md bg-accent/10 text-accent border border-accent/30 flex items-center justify-center mb-4">
            <Camera size={24} />
          </div>
          <div className="text-text font-medium">No HOP pack yet</div>
          <div className="text-[13px] text-text-muted mt-2 max-w-md mx-auto">
            When a manager creates a HOP pack for this project, you'll see it here and can start uploading photos + documents (RLMs, cable sweeps) against each category.
          </div>
          <div className="mt-4">
            <Link to={`/projects/${projectId}/hop`}>
              <Button variant="secondary" icon={Camera}>Open HOP library</Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {packs.map((p: any) => (
        <Link key={p.pack_id} to={`/projects/${projectId}/hop/${p.pack_id}`}>
          <Card className="hover:border-line-light transition-colors">
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
                <Camera size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono text-text-muted">{p.pack_number}</span>
                  <span className="text-[10px] uppercase tracking-wider2 font-mono text-text-dim">
                    {p.template?.template_code ?? "—"}
                  </span>
                </div>
                <div className="text-text mt-0.5">
                  {p.title || p.template?.display_name || "HOP pack"}
                </div>
              </div>
              <Pill label={packLabel[p.status] ?? p.status} tone={packTone[p.status] ?? "default"} small />
              <ExternalLink size={14} className="text-text-muted flex-shrink-0" />
            </div>
          </Card>
        </Link>
      ))}
      <div className="pt-2 text-center">
        <Link to={`/projects/${projectId}/hop`} className="text-[12px] text-accent hover:underline">
          Open full HOP library →
        </Link>
      </div>
    </div>
  );
}
